import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, sendPasswordResetEmail } from 'firebase/auth'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from 'sonner'
import { auth } from '@/frontend/lib/firebase'
import { useAuth } from '@/frontend/contexts/AuthContext'

/**
 * Sign-in page component with Firebase authentication
 * Supports email/password and Google sign-in
 */
export default function SignInPage() {
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [showForgotPassword, setShowForgotPassword] = useState(false)
	const [resetEmail, setResetEmail] = useState('')
	const navigate = useNavigate()
	const { isSignedIn, signInWithLine, getLineAuthError } = useAuth()

	// Redirect if already signed in
	if (isSignedIn) {
		navigate('/', { replace: true })
		return null
	}

	const handleEmailSignIn = async (e: React.FormEvent) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		try {
			await signInWithEmailAndPassword(auth, email, password)
			console.log('[Auth] Email sign-in successful')
			navigate('/', { replace: true })
		} catch (error: any) {
			console.error('[Auth] Email sign-in error:', error)
			setError(getErrorMessage(error.code))
		} finally {
			setLoading(false)
		}
	}

	const handleGoogleSignIn = async () => {
		setLoading(true)
		setError('')

		try {
			const provider = new GoogleAuthProvider()
			await signInWithPopup(auth, provider)
			console.log('[Auth] Google sign-in successful')
			navigate('/', { replace: true })
		} catch (error: any) {
			console.error('[Auth] Google sign-in error:', error)
			setError(getErrorMessage(error.code))
		} finally {
			setLoading(false)
		}
	}

	const handleLineSignIn = async () => {
		setLoading(true)
		setError('')

		try {
			await signInWithLine()
			console.log('[Auth] LINE sign-in initiated')
		} catch (error: any) {
			console.error('[Auth] LINE sign-in error:', error)
			setError(getLineAuthError(error))
			setLoading(false)
		}
	}

	const handleForgotPassword = async (e: React.FormEvent) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		try {
			await sendPasswordResetEmail(auth, resetEmail)
			toast.success('Password reset email sent! Please check your inbox.')
			setShowForgotPassword(false)
			setResetEmail('')
		} catch (error: any) {
			console.error('[Auth] Password reset error:', error)
			setError(getErrorMessage(error.code))
		} finally {
			setLoading(false)
		}
	}

	const getErrorMessage = (errorCode: string) => {
		switch (errorCode) {
			case 'auth/user-not-found':
			case 'auth/wrong-password':
			case 'auth/invalid-credential':
				return 'Invalid email or password. Please try again.'
			case 'auth/invalid-email':
				return 'Please enter a valid email address.'
			case 'auth/user-disabled':
				return 'This account has been disabled. Please contact support.'
			case 'auth/too-many-requests':
				return 'Too many failed attempts. Please try again later.'
			case 'auth/popup-closed-by-user':
				return 'Sign-in was cancelled. Please try again.'
			// LINE-specific errors
			case 'line/access_denied':
				return 'LINE login was cancelled. Please try again.'
			case 'line/invalid_request':
				return 'LINE login request was invalid. Please try again.'
			case 'line/unauthorized_client':
				return 'LINE login is not properly configured. Please contact support.'
			case 'line/network_error':
				return 'Network error occurred during LINE login. Please check your connection.'
			default:
				return 'An error occurred during sign-in. Please try again.'
		}
	}

	return (
		<div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-5">
			<Card className="w-full max-w-md shadow-2xl border-0 rounded-xl">
				<CardHeader className="text-center">
					<CardTitle className="text-2xl font-bold text-blue-600">
						Document Processor
					</CardTitle>
					<CardDescription className="text-gray-600">
						Thai Court Document Processing System
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{error && (
						<Alert variant="destructive">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					<form onSubmit={handleEmailSignIn} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="Enter your email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								disabled={loading}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Input
									id="password"
									type={showPassword ? 'text' : 'password'}
									placeholder="Enter your password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									disabled={loading}
								/>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
									onClick={() => setShowPassword(!showPassword)}
									disabled={loading}
								>
									<FontAwesomeIcon
										icon={showPassword ? faEyeSlash : faEye}
										className="h-4 w-4 text-gray-400"
									/>
								</Button>
							</div>
						</div>

						<Button type="submit" className="w-full" disabled={loading}>
							{loading ? 'Signing in...' : 'Sign in with Email'}
						</Button>
					</form>

					{/* Forgot Password Section */}
					{!showForgotPassword ? (
						<div className="text-center">
							<Button
								variant="link"
								className="p-0 text-sm text-blue-600 hover:text-blue-800"
								onClick={() => setShowForgotPassword(true)}
								disabled={loading}
							>
								Forgot your password?
							</Button>
						</div>
					) : (
						<Card className="border border-blue-200 bg-blue-50">
							<CardContent className="pt-6">
								<form onSubmit={handleForgotPassword} className="space-y-4">
									<div>
										<Label htmlFor="resetEmail">Email Address</Label>
										<Input
											id="resetEmail"
											type="email"
											placeholder="Enter your email to reset password"
											value={resetEmail}
											onChange={(e) => setResetEmail(e.target.value)}
											required
											disabled={loading}
										/>
									</div>
									<div className="flex gap-2">
										<Button type="submit" disabled={loading} className="flex-1">
											{loading ? 'Sending...' : 'Send Reset Email'}
										</Button>
										<Button
											type="button"
											variant="ghost"
											onClick={() => {
												setShowForgotPassword(false)
												setResetEmail('')
												setError('')
											}}
											disabled={loading}
										>
											Cancel
										</Button>
									</div>
								</form>
							</CardContent>
						</Card>
					)}

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-background px-2 text-muted-foreground">Or continue with</span>
						</div>
					</div>

					<Button
						type="button"
						className="w-full bg-white hover:bg-gray-50 text-[#1F1F1F] border border-[#747775] transition-colors h-10 px-3 flex items-center justify-center gap-2.5"
						onClick={handleGoogleSignIn}
						disabled={loading}
						style={{ 
							fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
							fontSize: '14px',
							fontWeight: 500,
							lineHeight: '20px'
						}}
					>
						<svg className="w-[18px] h-[18px] flex-shrink-0" viewBox="0 0 24 24" aria-label="Google logo">
							<title>Google</title>
							<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
							<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
							<path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
							<path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
						</svg>
						<span>{loading ? 'Signing in...' : 'Sign in with Google'}</span>
					</Button>

					{/* LINE Login Button following official guidelines */}
					<Button
						type="button"
						className="w-full border-0 font-medium transition-all duration-150 flex items-center justify-center relative overflow-hidden rounded-md"
						style={{
							backgroundColor: loading ? '#FFFFFF' : '#06C755',
							color: loading ? 'rgba(30, 30, 30, 0.2)' : '#FFFFFF',
							border: loading ? '1px solid rgba(229, 229, 229, 0.6)' : 'none',
							// Official LINE padding guidelines: padding should be >= icon width, height = X/2
							paddingTop: '11px', // 44dp icon height / 4 = 11px (X/2)
							paddingBottom: '11px',
							paddingLeft: '44px', // >= icon width (44px)
							paddingRight: '44px',
							height: '48px', // Sufficient height for 44dp icon + padding
							fontSize: '14px',
							fontWeight: 500
						}}
						onMouseEnter={(e) => {
							if (!loading) {
								// Hover: #06C755 + 10% black overlay = rgba(6, 199, 85, 0.9) + rgba(0, 0, 0, 0.1)
								e.currentTarget.style.backgroundColor = 'rgb(5, 179, 76)' // #05b34c
							}
						}}
						onMouseLeave={(e) => {
							if (!loading) {
								e.currentTarget.style.backgroundColor = '#06C755'
							}
						}}
						onMouseDown={(e) => {
							if (!loading) {
								// Press: #06C755 + 30% black overlay
								e.currentTarget.style.backgroundColor = 'rgb(4, 139, 59)' // #048b3b
							}
						}}
						onMouseUp={(e) => {
							if (!loading) {
								// Return to hover state
								e.currentTarget.style.backgroundColor = 'rgb(5, 179, 76)'
							}
						}}
						onClick={handleLineSignIn}
						disabled={loading}
					>
						{/* Official LINE icon with proper sizing and spacing */}
						<img 
							src="/src/frontend/assets/line_44.png" 
							srcSet="/src/frontend/assets/line_88.png 2x"
							alt="LINE" 
							className="absolute left-3 top-1/2 transform -translate-y-1/2"
							style={{
								width: '22px', // 44dp / 2 for proper scale in web
								height: '22px',
								objectFit: 'contain'
							}}
						/>
						{/* Vertical line separator as per guidelines */}
						{!loading && (
							<div 
								className="absolute left-12 top-2 bottom-2"
								style={{
									width: '1px',
									backgroundColor: 'rgba(0, 0, 0, 0.08)'
								}}
							/>
						)}
						<span className="ml-6">{loading ? 'Signing in...' : 'Log in with LINE'}</span>
					</Button>

					{/* <div className="text-center text-sm text-gray-600">
						Don't have an account?{' '}
						<Button
							variant="link"
							className="p-0 text-blue-600 hover:text-blue-800"
							onClick={() => navigate('/sign-up')}
							disabled={loading}
						>
							Sign up
						</Button>
					</div> */}
				</CardContent>
			</Card>
		</div>
	)
}
